<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { firstDayOfMonth, toIsoDate } from '../utils/format'
import type { PayrollPeriod } from '../types/finance'

/** `/payroll` — payroll periods (open → locked → closed). */

const { t } = useI18n()
const auth = useAuthStore()
const {
  listPayrollPeriods,
  createPayrollPeriod,
  updatePayrollPeriod,
  lockPayrollPeriod,
  closePayrollPeriod,
} = useFinanceApi()

const PER_PAGE = 20
const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('payroll_periods.manage'))
const canClose = computed(() => permissions.value.includes('payroll_periods.manage'))

const rows = ref<PayrollPeriod[]>([])
const total = ref(0)
const totalPages = ref(1)
const page = ref(1)
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busyId = ref<string | number | null>(null)

const filters = ref({ status: '', search: '' })

const formOpen = ref(false)
const saving = ref(false)
const editingId = ref<string | number | null>(null)
const form = reactive({
  code: '',
  name: '',
  date_from: firstDayOfMonth(),
  date_to: toIsoDate(),
})

const formError = computed(() => {
  if (!form.code.trim() || !form.name.trim()) return ''
  if (form.date_from > form.date_to) return t('finance.common.invalidRange')
  return ''
})

const rangeLabel = computed(() => {
  if (!total.value) return ''
  const from = (page.value - 1) * PER_PAGE + 1
  return t('finance.common.rows', { count: total.value }) +
    ` · ${t('finance.common.page', { page: page.value, total: totalPages.value })}` +
    ` · ${from}–${Math.min(page.value * PER_PAGE, total.value)}`
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listPayrollPeriods({
      status: filters.value.status || undefined,
      search: filters.value.search.trim() || undefined,
      page: page.value,
      per_page: PER_PAGE,
    })
    rows.value = result.items
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
    rows.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

function openCreate() {
  editingId.value = null
  form.code = ''
  form.name = ''
  form.date_from = firstDayOfMonth()
  form.date_to = toIsoDate()
  formOpen.value = true
  actionError.value = ''
  actionMessage.value = ''
}

function openEdit(period: PayrollPeriod) {
  editingId.value = period.id
  form.code = period.code
  form.name = period.name
  form.date_from = period.date_from
  form.date_to = period.date_to
  formOpen.value = true
  actionError.value = ''
  actionMessage.value = ''
}

async function submit() {
  actionError.value = ''
  actionMessage.value = ''
  if (!form.code.trim() || !form.name.trim()) {
    actionError.value = t('finance.common.requiredField', { field: t('finance.payrollFlow.periods.code') })
    return
  }
  if (formError.value) {
    actionError.value = formError.value
    return
  }
  saving.value = true
  try {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      date_from: form.date_from,
      date_to: form.date_to,
    }
    if (editingId.value === null) {
      await createPayrollPeriod(payload)
      actionMessage.value = t('finance.payrollFlow.periods.created')
    } else {
      await updatePayrollPeriod(editingId.value, payload)
      actionMessage.value = t('finance.payrollFlow.periods.updated')
    }
    formOpen.value = false
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

async function lock(period: PayrollPeriod) {
  if (!window.confirm(t('finance.payrollFlow.periods.lockConfirm', { code: period.code }))) return
  busyId.value = period.id
  actionError.value = ''
  try {
    await lockPayrollPeriod(period.id)
    actionMessage.value = t('finance.payrollFlow.periods.locked')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = null
  }
}

async function close(period: PayrollPeriod) {
  if (!window.confirm(t('finance.payrollFlow.periods.closeConfirm', { code: period.code }))) return
  busyId.value = period.id
  actionError.value = ''
  try {
    await closePayrollPeriod(period.id)
    actionMessage.value = t('finance.payrollFlow.periods.closed')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = null
  }
}

watch(
  () => [filters.value.status, filters.value.search],
  () => {
    page.value = 1
    void load()
  },
)

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('finance.payrollFlow.periods.eyebrow') }}</p>
      <h2>{{ t('finance.payrollFlow.periods.title') }}</h2>
      <p>{{ t('finance.payrollFlow.periods.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <select v-model="filters.status" class="filter-select">
      <option value="">{{ t('finance.common.allStatuses') }}</option>
      <option value="open">{{ t('finance.payrollFlow.statusOpen') }}</option>
      <option value="locked">{{ t('finance.payrollFlow.statusLocked') }}</option>
      <option value="closed">{{ t('finance.payrollFlow.statusClosed') }}</option>
    </select>
    <input
      v-model="filters.search"
      class="search-input"
      type="search"
      :placeholder="t('finance.common.searchPlaceholder')"
    >
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.refresh') }}
    </button>
    <span class="spacer" />
    <button v-if="canManage" class="btn-primary" type="button" @click="openCreate">
      {{ t('finance.payrollFlow.periods.newPeriod') }}
    </button>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <section v-if="formOpen" class="form-card">
    <h3>
      {{ editingId === null ? t('finance.payrollFlow.periods.newPeriod') : t('finance.payrollFlow.periods.editPeriod') }}
      <small>{{ t('finance.payrollFlow.periods.formHint') }}</small>
    </h3>
    <div class="form-grid tight">
      <div class="field">
        <label for="period-code">{{ t('finance.payrollFlow.periods.code') }}</label>
        <input id="period-code" v-model="form.code" type="text" placeholder="PAY-2026-09">
      </div>
      <div class="field">
        <label for="period-name">{{ t('finance.payrollFlow.periods.name') }}</label>
        <input id="period-name" v-model="form.name" type="text">
      </div>
      <div class="field">
        <label for="period-from">{{ t('finance.common.dateFrom') }}</label>
        <input id="period-from" v-model="form.date_from" type="date">
      </div>
      <div class="field">
        <label for="period-to">{{ t('finance.common.dateTo') }}</label>
        <input id="period-to" v-model="form.date_to" type="date">
      </div>
    </div>
    <p v-if="formError" class="error-inline">{{ formError }}</p>
    <div class="modal-actions">
      <button class="btn-secondary" type="button" @click="formOpen = false">
        {{ t('finance.common.cancel') }}
      </button>
      <button class="btn-primary" type="button" :disabled="saving || Boolean(formError)" @click="submit">
        {{ saving ? t('finance.common.saving') : t('finance.common.save') }}
      </button>
    </div>
  </section>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <p v-else-if="!rows.length" class="state-message">{{ t('finance.payrollFlow.periods.empty') }}</p>

  <template v-else>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.payrollFlow.periods.code') }}</th>
            <th>{{ t('finance.payrollFlow.periods.name') }}</th>
            <th>{{ t('finance.payrollFlow.periods.range') }}</th>
            <th>{{ t('finance.common.status') }}</th>
            <th>{{ t('finance.payrollFlow.periods.lockedAt') }}</th>
            <th class="cell-actions">{{ t('finance.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="period in rows" :key="period.id">
            <td class="cell-code">{{ period.code }}</td>
            <td class="cell-name">{{ period.name }}</td>
            <td class="cell-date">
              {{ formatDisplayDate(period.date_from) }} – {{ formatDisplayDate(period.date_to) }}
            </td>
            <td>
              <span class="status-badge" :class="`status-${period.status}`">
                {{ t(`finance.payrollFlow.status${period.status.charAt(0).toUpperCase()}${period.status.slice(1)}`) }}
              </span>
            </td>
            <td class="cell-date">{{ period.locked_at ? formatDisplayDate(period.locked_at) : '—' }}</td>
            <td class="cell-actions">
              <button
                v-if="canManage && period.status === 'open'"
                class="link-action"
                type="button"
                @click="openEdit(period)"
              >
                {{ t('finance.common.edit') }}
              </button>
              <button
                v-if="canManage && period.status === 'open'"
                class="link-action"
                type="button"
                :disabled="busyId === period.id"
                @click="lock(period)"
              >
                {{ t('finance.payrollFlow.periods.lock') }}
              </button>
              <button
                v-if="canClose && period.status === 'locked'"
                class="link-action"
                type="button"
                :disabled="busyId === period.id"
                @click="close(period)"
              >
                {{ t('finance.payrollFlow.periods.close') }}
              </button>
              <span v-if="period.status === 'closed'" class="empty-inline">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <span>{{ rangeLabel }}</span>
      <button class="btn-secondary" type="button" :disabled="page <= 1" @click="page = page - 1; load()">
        {{ t('finance.common.previous') }}
      </button>
      <button
        class="btn-secondary"
        type="button"
        :disabled="page >= totalPages"
        @click="page = page + 1; load()"
      >
        {{ t('finance.common.next') }}
      </button>
    </div>
  </template>
</template>

<style scoped>
.status-open {
  border-color: #4e6651;
  color: #2f4a33;
  background: #eef4f0;
}

.status-locked {
  border-color: #d9a441;
  color: #7a5510;
  background: #fffaf0;
}

.status-closed {
  border-color: #cfd8d1;
  color: #5f6b62;
  background: #f6f8f6;
}
</style>
